import React from 'react';
import {
  Animated,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../theme/themeTokens';
import { COLORS_WRONG } from '../theme/colorsWrong';
import { useSwipeDownDismiss } from '../hooks/useSwipeDownDismiss';

// Standalone numeric keypad component — modeled on the keypad in
// ConfirmEmailScreen so the look matches the rest of the app. Self-contained:
// the parent only owns the value string and handles the onChange callback.
// Designed as a bottom-sheet so it can be reused for any amount input.
export interface NumericKeypadProps {
  visible: boolean;
  value: string;
  onChange: (next: string) => void;
  onClose: () => void;
  /**
   * Cap fractional digits. Defaults to 2 — matches the display cap used by
   * DepositCardScreen's From/To amount fields. Pass a larger number for
   * cases that need more precision (e.g. crypto amounts).
   */
  maxDecimals?: number;
  /**
   * Decimal separator the keypad emits. Defaults to ',' to match the
   * existing display format ("1 234,56"). Use '.' if the consumer's
   * formatter expects a dot.
   */
  decimalSeparator?: '.' | ',';
  /**
   * Optional pre-formatted "Available balance" string shown above the
   * keypad (e.g. "5 000,00 USDT"). Hides the row entirely when omitted.
   */
  availableValue?: string;
  /** Label for the Available row. Defaults to "Available balance". */
  availableLabel?: string;
  /**
   * Fired when the user taps "Maximum". The parent decides what value to
   * push (usually the full available balance of the selected account) —
   * the keypad doesn't compute it.
   */
  onMax?: () => void;
  /**
   * Fired when the user taps "Clean". Parent typically resets the bound
   * field to '' (or '0' depending on its display rules).
   */
  onClean?: () => void;
  /**
   * Rendering mode. Default (`false`) uses a Modal with a tap-to-dismiss
   * overlay — fine when the host screen has a single input. Set to `true`
   * to render the sheet inline as an absolute-positioned View at the
   * bottom of the parent; the host screen then keeps full interactivity
   * (tap-targets above stay tappable, switching between two amount
   * inputs doesn't dismiss the keypad). The host is responsible for the
   * "click outside closes" behaviour in inline mode — see DepositCardScreen.
   */
  inline?: boolean;
  /**
   * When `true`, the modal-mode backdrop is rendered fully transparent
   * (instead of the default 25 % dark scrim). Tap-to-dismiss still
   * works — only the visual dimming is suppressed. Has no effect in
   * `inline` mode (there's no overlay there).
   */
  transparentOverlay?: boolean;
}

const NumericKeypad: React.FC<NumericKeypadProps> = ({
  visible,
  value,
  onChange,
  onClose,
  maxDecimals = 2,
  decimalSeparator = ',',
  availableValue,
  availableLabel = 'Available balance',
  onMax,
  onClean,
  inline = false,
  transparentOverlay = false,
}) => {
  // Swipe-to-dismiss — every host screen (Withdraw / Open / TopUp /
  // Outflow / DepositCard) gets the same behaviour for free via the
  // shared hook. See `useSwipeDownDismiss` for the gesture mechanics.
  const { translateY, panHandlers } = useSwipeDownDismiss(visible, onClose);
  // Append helper handles three concerns at once:
  //   1) "0" replacement on first non-separator digit ("0" → "5", not "05");
  //   2) at most one decimal separator;
  //   3) the maxDecimals cap.
  // Both '.' and ',' are accepted in the input string for detection so the
  // keypad works when the parent's formatter rewrites the separator.
  const append = (k: string) => {
    const sep = decimalSeparator;
    const v = value ?? '';
    if (k === sep) {
      if (v.includes(',') || v.includes('.')) return;
      onChange(v === '' ? '0' + sep : v + sep);
      return;
    }
    if (v === '0') {
      onChange(k);
      return;
    }
    // Cap on fractional digits — looks for either separator since the parent
    // may rewrite ',' to '.' (or vice versa) before passing value back in.
    const sepIdx = (() => {
      const i = v.indexOf(',');
      return i !== -1 ? i : v.indexOf('.');
    })();
    if (sepIdx !== -1) {
      // Count digits after the separator, ignoring any non-digit chars the
      // parent's display formatter might have inserted (thousands spaces).
      const fracDigits = v.slice(sepIdx + 1).replace(/\D/g, '');
      if (fracDigits.length >= maxDecimals) return;
    }
    onChange(v + k);
  };

  const backspace = () => {
    if (!value) return;
    onChange(value.slice(0, -1));
  };

  // Sheet body — shared between Modal mode and inline mode. The inline
  // path doesn't need stopPropagation since there's no overlay wrapping
  // it; Modal mode keeps the stopPropagation so an in-sheet tap doesn't
  // bubble to the overlay's onClose.
  const sheetContent = (
    <>
      {/* The sheet can be swiped down — the hook above wires the gesture —
          but nothing said so. The same grabber the account pickers and the
          history sheet use, in blueSteel: softGrey and borderMuted vanish
          against this sheet's own grey. */}
      <View style={styles.grabber} />

      {/* Available balance row — label left, amount right. */}
          {availableValue ? (
            <View style={styles.availableRow}>
              <Text style={styles.availableLabel}>{availableLabel}</Text>
              <Text style={styles.availableValue}>{availableValue}</Text>
            </View>
          ) : null}

          {/* Maximum + Clean — appear when at least one handler is provided. */}
          {(onMax || onClean) ? (
            <View style={styles.actionRow}>
              {onMax ? (
                <TouchableOpacity
                  style={[styles.actionBtn, styles.actionBtnMax]}
                  onPress={onMax}
                  activeOpacity={0.85}
                >
                  <Text style={styles.actionBtnText}>Maximum</Text>
                </TouchableOpacity>
              ) : null}
              {onClean ? (
                <TouchableOpacity
                  style={[styles.actionBtn, styles.actionBtnClean]}
                  onPress={onClean}
                  activeOpacity={0.85}
                >
                  <Text style={styles.actionBtnText}>Clean</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {[['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9']].map((row) => (
            <View key={row.join('')} style={styles.row}>
              {row.map((k) => (
                <TouchableOpacity
                  key={k}
                  style={styles.key}
                  onPress={() => append(k)}
                  activeOpacity={0.6}
                >
                  <Text style={styles.keyText}>{k}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ))}

          {/* Bottom row — separator and backspace are "ghost" keys (no white
              tile), 0 keeps the white tile. Same column metrics as the digit
              rows so everything aligns. */}
          <View style={styles.row}>
            <TouchableOpacity
              style={styles.keyGhost}
              onPress={() => append(decimalSeparator)}
              activeOpacity={0.6}
            >
              <Text style={styles.keyText}>{decimalSeparator}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.key}
              onPress={() => append('0')}
              activeOpacity={0.6}
            >
              <Text style={styles.keyText}>0</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.keyGhost}
              onPress={backspace}
              activeOpacity={0.6}
            >
              {/* Unicode "ERASE TO THE LEFT" with a text-presentation
                  selector after it. Without the selector the platform draws
                  the glyph as an emoji — coloured, and deaf to the style's
                  colour, which is where the stray orange corner came from. */}
              <Text style={styles.backspaceIcon}>{'\u232B\uFE0E'}</Text>
            </TouchableOpacity>
          </View>
    </>
  );

  // Inline mode: render as an absolute-positioned bottom sheet inside the
  // host's coordinate space. No overlay, no Modal — tap-targets above stay
  // tappable, and the host owns "click outside closes" via its own
  // Pressable wrap. Returns null when not visible so the surface
  // disappears entirely on close.
  if (inline) {
    if (!visible) return null;
    return (
      <Animated.View
        style={[styles.sheet, styles.sheetInline, { transform: [{ translateY }] }]}
        {...panHandlers}
      >
        {sheetContent}
      </Animated.View>
    );
  }

  // Modal mode (default): full-screen tap-to-dismiss overlay + bottom sheet.
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable
        style={[styles.overlay, transparentOverlay && styles.overlayTransparent]}
        onPress={onClose}
      >
        {/* stopPropagation: tapping the sheet itself must not close it.
            translateY + panResponder wire up the swipe-to-dismiss. The
            Animated.View intercepts only downward drags (see the
            PanResponder above); ordinary key taps still go through. */}
        <Animated.View
          style={[styles.sheet, { transform: [{ translateY }] }]}
          {...panHandlers}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
            {sheetContent}
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  /* Opt-in override — set `transparentOverlay` on the host to drop
     the dark scrim entirely (host screen stays fully visible behind
     the keypad sheet). Tap-to-dismiss still works through the
     transparent layer. */
  overlayTransparent: {
    backgroundColor: 'transparent',
  },
  /* Sheet: iOS-style light-gray surface (looks like system gray 5/6 in the
     mockup). The white digit tiles sit on top of this with a subtle shadow,
     producing the layered "raised key" look. */
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.blueSteel,
    alignSelf: 'center',
    marginBottom: 12,
  },
  sheet: {
    backgroundColor: '#E5E5EA',
    paddingHorizontal: 12,
    paddingTop: 16,
    paddingBottom: 8,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  /* Inline mode — pin flush to the bottom of the parent's coordinate
     space. zIndex/elevation keep it above the scroll content AND the
     TabBar (which has its own absolute-position layer).
     `bottom: -34` on iOS pushes the sheet INTO the home-indicator gutter
     so the bottom edge of the sheet reaches the device bottom — same
     visual landing as the Modal-mode keypad on WithdrawScreen (which
     renders below SafeAreaView). The base sheet style's
     `paddingBottom: 8` is preserved, so the gap below the last row of
     keys is identical to Withdraw's Modal keypad. Android has no
     gutter, stays at 0. */
  sheetInline: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: Platform.OS === 'ios' ? -34 : 0,
    zIndex: 10,
    elevation: 10,
  },
  /* Available row — header strip. No background, sits on the sheet's gray. */
  availableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 4,
    marginBottom: 12,
  },
  availableLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.steelNavy,
  },
  availableValue: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  /* Action row — Max + Clean. Tall, rounded, full-row width. */
  actionRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  actionBtn: {
    flex: 1,
    height: 48,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionBtnMax: { backgroundColor: COLORS.brandOrange },
  actionBtnClean: { backgroundColor: COLORS.primaryNavy },
  actionBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.white,
  },
  /* Row of three equal-width keys. The horizontal gap between keys is added
     via marginHorizontal on each key (RN gap on row works too, kept explicit
     for older RN versions). */
  row: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  /* Digit key — white raised tile, rounded 10. flex:1 so 3 keys span the
     full row width with the 10-px gaps. iOS gets a soft shadow; Android
     uses elevation so the same depth shows up on both. */
  key: {
    flex: 1,
    height: 56,
    backgroundColor: COLORS.white,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 0.5,
    elevation: 1,
  },
  /* "Ghost" key — same footprint as a digit tile so columns stay aligned,
     but no background or shadow (used for the comma + backspace slots). */
  keyGhost: {
    flex: 1,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  keyText: {
    fontFamily: Platform.select({
      ios: 'HelveticaNeue',
      android: 'sans-serif',
    }),
    fontSize: 26,
    fontWeight: '400',
    lineHeight: 30,
    color: COLORS_WRONG.keypadText,
  },
  /* Backspace glyph — Unicode U+232B. Sized to read like the digit keys. */
  backspaceIcon: {
    fontSize: 28,
    lineHeight: 30,
    // The same blue as the digits — it is a key like any other.
    color: COLORS_WRONG.keypadText,
  },
});

export default NumericKeypad;
